// The Portfolio mode's shared pieces (FOUNDER-OS; 12): the relation badge ("Client via <agency>"), the
// KPI cell (health dot, score, trend), the needs-you chips, the budget bar and small formatters.
// Exceptions are loud (danger and warning tones), healthy rows quiet (muted text, no colour).
import { Badge, cn, Progress } from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { useRouteContext } from "@tanstack/react-router";
import { ArrowDownRightIcon, ArrowRightIcon, ArrowUpRightIcon } from "lucide-react";
import type { ReactNode } from "react";
import type { FocusRow, NeedsYou } from "../../api/types";
import { HealthDot } from "../../shell/company-switcher";
import { formatMoney, formatScore } from "../format";
import { meCompaniesQuery } from "./portfolio-queries";

/** The agency each client company is reached through (the operator), from the switcher's list. */
export function useAgencyNames(): Map<string, string> {
  const { api } = useRouteContext({ from: "__root__" });
  const list = useQuery(meCompaniesQuery(api));
  return new Map(
    (list.data?.companies ?? []).flatMap((c) =>
      c.agency_name ? [[c.company_id, c.agency_name] as const] : [],
    ),
  );
}

export function RelationBadge({
  relation,
  agency,
}: {
  relation: FocusRow["relation"];
  agency?: string | null | undefined;
}) {
  return relation === "owned" ? (
    <Badge variant="outline">Owned</Badge>
  ) : (
    <Badge variant="info">{agency ? `Client via ${agency}` : "Client"}</Badge>
  );
}

const TREND = {
  up: { Icon: ArrowUpRightIcon, label: "up", tone: "text-success-text" },
  down: { Icon: ArrowDownRightIcon, label: "down", tone: "text-danger-text" },
  flat: { Icon: ArrowRightIcon, label: "flat", tone: "text-faint" },
} as const;

export function TrendArrow({ trend }: { trend: FocusRow["trend"] }) {
  if (!trend) return null;
  const t = TREND[trend];
  return (
    <t.Icon
      role="img"
      aria-label={`Trend ${t.label}`}
      data-trend={trend}
      className={cn("size-3.5 shrink-0", t.tone)}
    />
  );
}

/** A 0–1 fraction (goal progress) as a percent. The KPI score is 0–100: formatScore, not this. */
export const pct = (n: number) => `${Math.round(n * 100)}%`;

/** "1 person · 4 seats · 1 open" (the open part only when asked and non-zero). */
export function headcountText(
  h: { people: number; seats: number; open_seats: number },
  withOpen = true,
): string {
  const parts = [
    `${h.people} ${h.people === 1 ? "person" : "people"}`,
    `${h.seats} seat${h.seats === 1 ? "" : "s"}`,
  ];
  if (withOpen && h.open_seats) parts.push(`${h.open_seats} open`);
  return parts.join(" · ");
}

/** Health dot, KPI score and trend, in one line. */
export function KpiCell({ row }: { row: Pick<FocusRow, "band" | "kpi_score" | "trend"> }) {
  return (
    <span className="inline-flex items-center gap-1.5 tabular-nums" data-slot="kpi-cell">
      <HealthDot band={row.band} />
      <span className="font-medium text-foreground">
        {row.kpi_score === null ? "No KPI yet" : formatScore(row.kpi_score)}
      </span>
      <TrendArrow trend={row.trend} />
    </span>
  );
}

const NEED_TONE: Record<NeedsYou["kind"], "danger" | "warning" | "info"> = {
  kpi: "danger",
  priority: "warning",
  escalation: "danger",
  budget: "danger",
  approvals: "info",
  meeting: "warning",
  // FOUNDER-OS phase 1: the head seat is open; no numbers for 7 days.
  vacancy: "warning",
  stale: "warning",
};

/** "Withheld by Acme": a kind the company does not share with Founder OS. */
export function Withheld({ name }: { name: string }) {
  return (
    <span className="text-xs text-muted-foreground italic" data-slot="withheld">
      Withheld by {name}
    </span>
  );
}

export function NeedsChips({ needs }: { needs: readonly NeedsYou[] }) {
  if (needs.length === 0) return null;
  return (
    <ul className="flex flex-wrap gap-1.5" aria-label="Needs you" data-slot="needs-chips">
      {needs.map((n) => (
        <li key={n.kind}>
          <Badge variant={NEED_TONE[n.kind]} dot data-need={n.kind}>
            {n.text}
          </Badge>
        </li>
      ))}
    </ul>
  );
}

/** Budget vs actual as a thin bar: red past the budget. */
export function BudgetBar({ finance }: { finance: FocusRow["finance"] }) {
  if (!finance || finance.budget_minor === null)
    return <span className="text-muted-foreground">No budget</span>;
  const over = finance.actual_minor > finance.budget_minor;
  const share = finance.budget_minor > 0 ? finance.actual_minor / finance.budget_minor : 1;
  const money = (n: number) => formatMoney(n, finance.currency, { whole: true });
  return (
    <div
      className="flex min-w-0 flex-col gap-1"
      data-slot="budget-bar"
      data-over={over || undefined}
    >
      <span className="truncate tabular-nums">
        <span className={cn("font-medium", over ? "text-danger-text" : "text-foreground")}>
          {money(finance.actual_minor)}
        </span>
        <span className="text-muted-foreground"> of {money(finance.budget_minor)}</span>
      </span>
      <Progress
        value={Math.min(100, Math.round(share * 100))}
        aria-label="Budget used"
        tone={over ? "danger" : "default"}
        className="w-full"
      />
    </div>
  );
}

/** Agent spend this month ($182.40) from micros. */
export function formatSpend(micros: number): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
    micros / 1_000_000,
  );
}

/** "today", "tomorrow", "in 3 days", "2 days ago". */
export function relativeDay(iso: string, now = new Date()): string {
  const day = (d: Date) => Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  const diff = Math.round((day(new Date(iso)) - day(now)) / 86_400_000);
  if (diff === 0) return "today";
  if (diff === 1) return "tomorrow";
  if (diff === -1) return "yesterday";
  return diff > 0 ? `in ${diff} days` : `${-diff} days ago`;
}

/** A small labelled figure of a Focus row (label over value). */
export function Figure({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex min-w-0 flex-col gap-0.5", className)}>
      <span className="text-2xs font-medium tracking-wide text-faint uppercase">{label}</span>
      <div className="min-w-0 text-sm text-secondary">{children}</div>
    </div>
  );
}
